import { Update } from '@mui/icons-material';
import { Divider, Menu } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDeleteContent } from '~/api/content';
import { useUpgradeContentItem } from '~/api/content-items';
import { ActionButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import {
  MoreAction,
  MoreDeleteAction,
  mergeGridActionProps,
  type GridActionCellProps,
} from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useMoreActions } from '~/hooks/table';
import type { TemplateUsage__ContentItem as ContentItem } from '../../queries/TemplateUsage.generated';

export interface UsageMenuProps extends GridActionCellProps {
  contentItem: ContentItem;
}

export const UsageMenu = ({ contentItem, ...cellProps }: UsageMenuProps) => {
  const { t } = useTranslation();
  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<ContentItem>();
  const actionProps = mergeGridActionProps(moreTableActionProps(contentItem), cellProps);
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [confirmUpgrade, confirmUpgradeProps] = useConfirmDialog();

  const [upgradeContentItem] = useUpgradeContentItem();
  // Delete changes the row count driving pagination, which no mutation response can resolve.
  const [deleteContent] = useDeleteContent({ refetchQueries: ['TemplateUsage'] });

  const { canDestroy, canUpgrade } = contentItem;

  return (
    <>
      <ActionButton {...actionProps} />
      <Menu {...moreMenuProps}>
        <MoreAction
          {...moreActionProps}
          Icon={Update}
          disabled={!canUpgrade.value}
          title={t('pages.content.menu.upgrade')}
          tooltip={canUpgrade.reasons?.fullMessages.join(', ') ?? ''}
          onClick={async () => {
            if (!(await confirmUpgrade())) return;
            await upgradeContentItem({ variables: { contentItemId: contentItem.id } });
          }}
        />
        <Divider />
        <MoreDeleteAction
          {...moreActionProps}
          disabled={!canDestroy.value}
          tooltip={canDestroy.reasons?.fullMessages.join(', ') ?? ''}
          onClick={async () => {
            if (!(await confirmDelete())) return;
            await deleteContent({
              variables: { items: [{ contentableId: contentItem.id, contentableType: 'APP' }] },
            });
          }}
        />
      </Menu>
      <ConfirmDialog
        {...confirmUpgradeProps}
        confirm={t('common.dialogs.confirm')}
        prompt={t('pages.content.dialogs.upgrade_app.prompt')}
        title={t('pages.content.dialogs.upgrade_app.title')}
      />
      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('common.dialogs.delete')}
        deleteConfirm
        prompt={t('pages.content.dialogs.delete_folder.prompt_content')}
        title={t('pages.content.dialogs.delete_folder.title_content')}
      />
    </>
  );
};
